'use client'

import { useEffect, useState, type ComponentProps } from 'react'
import Image from 'next/image'
import { Menu, X } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Link, usePathname } from '@/i18n/navigation'
import { ROUTE_KEYS } from '@/i18n/routing'
import { resolveHref } from '@/lib/href'
import { cn } from '@/lib/cn'
import type { NavigationItem, NavigationTree, SiteSettings } from '@/types/content'
import { LinkButton } from '@/components/ui/Button'
import { Icon } from '@/components/ui/Icon'
import { LanguageSwitcher } from './LanguageSwitcher'

interface HeaderShellProps {
  settings: SiteSettings
  navigation: NavigationTree
}

function useIsActive() {
  const pathname = usePathname()
  return (item: NavigationItem) => {
    if (!item.routeKey) return false
    const target = ROUTE_KEYS[item.routeKey]
    return target === '/' ? pathname === '/' : pathname.startsWith(target)
  }
}

export function HeaderShell({ settings, navigation }: HeaderShellProps) {
  const t = useTranslations()
  const isActive = useIsActive()
  const prefersReducedMotion = useReducedMotion()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Empêche le défilement de l'arrière-plan quand le menu mobile est ouvert.
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMenuOpen])

  const renderLink = (item: NavigationItem, className: string, activeClassName: string) => {
    const target = resolveHref(item)
    const active = isActive(item)

    if (target.external) {
      return (
        <a key={item.id} href={target.href} className={className} rel="noreferrer noopener">
          {item.label}
        </a>
      )
    }

    return (
      <Link
        key={item.id}
        href={target.href as ComponentProps<typeof Link>['href']}
        className={cn(className, active && activeClassName)}
        aria-current={active ? 'page' : undefined}
      >
        {item.label}
      </Link>
    )
  }

  return (
    <>
      {/* Barre fine du menu secondaire — desktop uniquement. */}
      <div className="bg-surface-muted border-border hidden border-b lg:block">
        <div className="container-site flex h-9 items-center justify-end gap-6">
          <nav aria-label={t('common.secondaryNavigation')} className="flex items-center gap-6">
            {navigation.secondary.map((item) => {
              const target = resolveHref(item)
              const linkClass =
                'text-text-muted hover:text-primary-700 inline-flex items-center gap-1.5 text-xs font-medium transition-colors'
              return target.external ? (
                <a key={item.id} href={target.href} className={linkClass} rel="noreferrer noopener">
                  <Icon name={item.icon} className="size-3.5" />
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.id}
                  href={target.href as ComponentProps<typeof Link>['href']}
                  className={linkClass}
                >
                  <Icon name={item.icon} className="size-3.5" />
                  {item.label}
                </Link>
              )
            })}
          </nav>
        </div>
      </div>

      <header
        className={cn(
          'sticky top-0 z-50 transition-all duration-300 ease-[var(--ease-out-expo)]',
          isScrolled
            ? 'bg-surface/85 border-border border-b shadow-sm backdrop-blur-lg'
            : 'bg-surface/0 border-b border-transparent',
        )}
      >
        <div className="container-site flex h-20 items-center justify-between gap-6 md:h-22">
          <Link href="/" className="shrink-0" aria-label={settings.siteName}>
            <Image
              src={settings.logo.url}
              alt={settings.logo.alt}
              width={settings.logo.width ?? 160}
              height={settings.logo.height ?? 100}
              /*
                Ni `preload`, ni `loading="eager"` : les deux font émettre un
                `<link rel="preload">`, et le seul de l'en-tête revenait donc
                au logo, au détriment de la photo qui est l'élément LCP — un
                retard mesuré à 1,2 s. Le logo est dans la fenêtre : le
                navigateur le récupère immédiatement malgré le chargement
                différé, sans disputer la bande passante au hero.

                `sizes` est indispensable ici : sans lui, Next construit son
                jeu d'images à partir de la largeur RÉELLE du fichier (1000 px)
                et servait une variante de 2048 px pour un logo affiché à 70.
              */
              sizes="160px"
              className="h-11 w-auto md:h-13"
            />
          </Link>

          <nav
            aria-label={t('common.mainNavigation')}
            className="hidden items-center gap-1 lg:flex"
          >
            {navigation.primary.map((item) =>
              renderLink(
                item,
                'text-text-muted hover:text-primary-700 hover:bg-primary-50 rounded-full px-3.5 py-2 text-[0.9375rem] font-medium transition-colors duration-200',
                'text-primary-700 bg-primary-50',
              ),
            )}
          </nav>

          <div className="flex items-center gap-3">
            <LanguageSwitcher className="hidden sm:inline-flex" />
            <LinkButton
              cta={settings.demoCta}
              label={settings.demoCta.label}
              icon="ArrowRight"
              className="hidden md:inline-flex"
            />
            <button
              type="button"
              onClick={() => setIsMenuOpen((open) => !open)}
              className="text-ink-800 hover:bg-ink-100 inline-flex size-11 items-center justify-center rounded-full transition-colors lg:hidden"
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
              aria-label={isMenuOpen ? t('common.closeMenu') : t('common.openMenu')}
            >
              <Menu className="size-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {isMenuOpen ? (
          <motion.div
            id="mobile-menu"
            className="bg-surface fixed inset-0 z-60 overflow-y-auto lg:hidden"
            initial={prefersReducedMotion ? false : { opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: -12 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="container-site flex h-20 items-center justify-between md:h-22">
              <Image
                src={settings.logo.url}
                alt={settings.logo.alt}
                width={settings.logo.width ?? 160}
                height={settings.logo.height ?? 100}
                sizes="160px"
                className="h-11 w-auto"
              />
              <button
                type="button"
                onClick={() => setIsMenuOpen(false)}
                className="text-ink-800 hover:bg-ink-100 inline-flex size-11 items-center justify-center rounded-full transition-colors"
                aria-label={t('common.closeMenu')}
              >
                <X className="size-5" />
              </button>
            </div>

            {/* Tout clic sur un lien referme le menu — pas d'effet de bord au routage. */}
            <div className="container-site pb-12" onClick={() => setIsMenuOpen(false)}>
              <nav aria-label={t('common.mainNavigation')} className="flex flex-col">
                {navigation.primary.map((item) =>
                  renderLink(
                    item,
                    'border-border text-ink-900 border-b py-4 text-lg font-semibold',
                    'text-primary-700',
                  ),
                )}
              </nav>

              {/* Menu secondaire : en BAS du menu mobile (cahier des charges § 6). */}
              <nav
                aria-label={t('common.secondaryNavigation')}
                className="mt-8 flex flex-col gap-3"
              >
                {navigation.secondary.map((item) => {
                  const target = resolveHref(item)
                  return (
                    <Link
                      key={item.id}
                      href={target.href as ComponentProps<typeof Link>['href']}
                      className="bg-surface-muted text-ink-800 inline-flex items-center gap-2.5 rounded-xl px-4 py-3 text-sm font-semibold"
                    >
                      <Icon name={item.icon} className="text-primary-600 size-4.5" />
                      {item.label}
                    </Link>
                  )
                })}
              </nav>

              <div className="mt-8 flex items-center justify-between gap-4">
                <LanguageSwitcher />
                <LinkButton
                  cta={settings.demoCta}
                  label={settings.demoCta.label}
                  icon="ArrowRight"
                />
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  )
}
